/** @typedef {import("./types").Result} Result */
/** @typedef {import("./types").FetchOptions} FetchOptions */
/** @typedef {import("./types").IHttpClient} IHttpClient */

import { contentType } from "./headers";

/** @type {(baseUrl: string, url: string, query?: {}) => URL} */
const buildUrl = (baseUrl, url, query) =>
	Object.assign(new URL(url, baseUrl), query != null ? { search: new URLSearchParams(query) } : {});

const defaultContentType = data =>
	// prettier-ignore
	data === undefined ? undefined :
	typeof data === "string" ? "text/plain" :
	"application/json";

/** @type {(data: any) => string | undefined} */
const serialize = data =>
	typeof data !== "string" && data !== undefined ? JSON.stringify(data) : data;

/** @type {(response: Response) => Promise<Result>} */
const parseResponse = response =>
	(/json/i.test(contentType(response))
		? response.json().catch(error => ({ error })) //
		: response.text()
	).then(body => ({
		ok: response.ok,
		status: response.status,
		statusText: response.statusText,
		body,
	}));

/** @type {(fetch: FetchFn, options: FetchOptions & { baseUrl?: string }) => IHttpClient} */
const httpClient = (fetchFn, { baseUrl, ...baseOptions } = {}) => {
	const send = (method, url, { data, ...options } = {}) =>
		fetchFn(buildUrl(baseUrl, url, options.query).href, {
			...baseOptions,
			...options,
			method,
			headers: {
				...(data !== undefined
					? {
							"content-type": defaultContentType(data),
					  }
					: {}),
				...baseOptions.headers,
				...options.headers,
			},
			...(data !== undefined
				? {
						body: serialize(data),
				  }
				: {}),
		}).then(parseResponse);

	const sendEmpty = method => send.bind(undefined, method);
	const sendData = method => (url, data, options) => send(method, url, { ...options, data });

	return {
		get: sendEmpty("GET"),
		post: sendData("POST"),
		put: sendData("PUT"),
		patch: sendData("PATCH"),
		delete: sendEmpty("DELETE"),
	};
};

export default httpClient;
